'use client';

import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/useToast';
import { Article, Publication } from './ArticlesManagement';
import PublicationStatus from './PublicationStatus';
import PublishToDropdown from './PublishToDropdown';
import { articlesApi } from '@/app/api/articles';
import {
  TagIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpCircleIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  ClipboardIcon,
  CheckIcon,
  ShareIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { ExternalLinkIcon } from 'lucide-react';
import TaggedSelect from '@/components/common/TaggedSelect';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { Tooltip as ReactTippyTooltip } from 'react-tippy';
import 'react-tippy/dist/tippy.css';

// Create a wrapper component for Tooltip to fix TypeScript issues
const Tooltip = ({ children, ...props }: { children: React.ReactNode } & any) => {
  return <ReactTippyTooltip {...props}>{children}</ReactTippyTooltip>;
};

interface ArticlesListProps {
  articles: Article[];
  selectedArticle: Article | null;
  onArticleSelect: (article: Article) => void;
  onEditArticle: (id: string) => void;
  onDeleteArticle: (id: string) => void;
  onPublishArticle?: (id: string) => void;
  onShareArticle?: (id: string) => void;
  onFeaturedSummary?: (article: Article) => void;
  isLoading: boolean;
  onRefresh?: () => void;
  availableTags: string[];
  onUpdateArticleTags: (articleId: string, newTags: string[]) => void;
  onUpdateArticleCategory: (articleId: string, newCategory: string) => void;
  categoryMap: Record<string, string>;
}

// Mock function to simulate AI summary generation
const generateAISummary = (content: string = '') => {
  // In a real implementation, this would call an AI service
  return 'This is an AI-generated summary of the article content. It provides a brief overview of the key points covered in the article, helping readers quickly understand what the article is about without reading the entire content.';
};

export default function ArticlesList({
  articles,
  selectedArticle,
  onArticleSelect,
  onEditArticle,
  onDeleteArticle,
  onPublishArticle,
  onShareArticle,
  onFeaturedSummary,
  isLoading,
  onRefresh,
  availableTags,
  onUpdateArticleTags,
  onUpdateArticleCategory,
  categoryMap,
}: ArticlesListProps) {
  // Track expanded rows, local loading state, and publication status
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [localLoading, setLocalLoading] = useState<Record<string, boolean>>({});
  const [publicationStatus, setPublicationStatus] = useState<Record<string, any[]>>({});
  const [copySuccess, setCopySuccess] = useState<Record<string, boolean>>({});
  const { addToast } = useToast();
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
  });
  const [justPublishedId, setJustPublishedId] = useState<string | null>(null);

  // Load publication status for a specific article
  const loadPublicationStatus = async (articleId: string) => {
    try {
      const publications = await articlesApi.getArticlePublications(articleId);
      setPublicationStatus(prev => ({
        ...prev,
        [articleId]: publications
      }));
    } catch (error) {
      console.error('Error loading publication status:', error);
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (articleId: string, event?: React.MouseEvent) => {
    // If event is provided, stop propagation
    if (event) {
      event.stopPropagation();
    }

    const isExpanded = !expandedRows[articleId];
    setExpandedRows(prev => ({
      ...prev,
      [articleId]: isExpanded
    }));

    // If expanding, load publication status
    if (isExpanded) {
      const article = articles.find(a => a.id === articleId);
      if (article && article.status === 'published') {
        loadPublicationStatus(articleId);
      }
    }
  };

  // Handle state change actions
  const handlePublish = (articleId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    // Use parent's publish handler if provided (unified slug modal flow)
    if (onPublishArticle) {
      setJustPublishedId(articleId);
      onPublishArticle(articleId);
    } else {
      // Fallback to old confirm dialog if no parent handler
      setConfirmState({
        isOpen: true,
        title: 'Publish Article',
        message: 'Are you sure you want to publish this article? This will make it live to the public.',
        onConfirm: async () => {
          try {
            setLocalLoading(prev => ({ ...prev, [articleId]: true }));
            setJustPublishedId(articleId);
            await articlesApi.publishArticle(articleId);
            addToast('Article published successfully!', 'success');
            loadPublicationStatus(articleId);
            if (onRefresh) onRefresh();
          } catch (error) {
            console.error('Error publishing article:', error);
            addToast('Failed to publish article. Please try again.', 'error');
          } finally {
            setLocalLoading(prev => ({ ...prev, [articleId]: false }));
          }
        },
      });
    }
  };

  useEffect(() => {
    if (!justPublishedId) return;

    const article = articles.find(a => a.id === justPublishedId);
    if (article && article.status === 'published') {
      setExpandedRows(prev => ({
        ...prev,
        [justPublishedId]: true,
      }));
      loadPublicationStatus(justPublishedId);
      setJustPublishedId(null);
    }
  }, [articles, justPublishedId]);

  const handleArchive = (articleId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setConfirmState({
      isOpen: true,
      title: 'Archive Article',
      message: 'Are you sure you want to archive this article? It will be removed from public view.',
      onConfirm: async () => {
        try {
          setLocalLoading(prev => ({ ...prev, [articleId]: true }));
          await articlesApi.updateArticle(articleId, { state: 'archived' });
          addToast('Article archived successfully!', 'success');
          if (onRefresh) onRefresh();
        } catch (error) {
          console.error('Error archiving article:', error);
          addToast('Failed to archive article. Please try again.', 'error');
        } finally {
          setLocalLoading(prev => ({ ...prev, [articleId]: false }));
        }
      },
    });
  };

  const handleClone = (articleId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setConfirmState({
      isOpen: true,
      title: 'Clone Article',
      message: 'Are you sure you want to clone this article? A new draft will be created.',
      onConfirm: async () => {
        try {
          setLocalLoading(prev => ({ ...prev, [articleId]: true }));
          await articlesApi.cloneArticle(articleId);
          addToast('Article cloned successfully!', 'success');
          if (onRefresh) onRefresh();
        } catch (error) {
          console.error('Error cloning article:', error);
          addToast('Failed to clone article. Please try again.', 'error');
        } finally {
          setLocalLoading(prev => ({ ...prev, [articleId]: false }));
        }
      },
    });
  };

  const handleRestore = (articleId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setConfirmState({
      isOpen: true,
      title: 'Restore Article',
      message: 'Are you sure you want to restore this article to a draft state?',
      onConfirm: async () => {
        try {
          setLocalLoading(prev => ({ ...prev, [articleId]: true }));
          await articlesApi.updateArticle(articleId, { state: 'draft' });
          addToast('Article restored to draft successfully!', 'success');
          if (onRefresh) onRefresh();
        } catch (error) {
          console.error('Error restoring article:', error);
          addToast('Failed to restore article. Please try again.', 'error');
        } finally {
          setLocalLoading(prev => ({ ...prev, [articleId]: false }));
        }
      },
    });
  };

  const handleUnpublish = (articleId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setConfirmState({
      isOpen: true,
      title: 'Unpublish Article',
      message: 'Are you sure you want to unpublish this article? It will be moved back to draft status and removed from public view.',
      onConfirm: async () => {
        try {
          setLocalLoading(prev => ({ ...prev, [articleId]: true }));
          await articlesApi.unpublishArticle(articleId);
          addToast('Article unpublished successfully!', 'success');
          if (onRefresh) onRefresh();
        } catch (error) {
          console.error('Error unpublishing article:', error);
          addToast('Failed to unpublish article. Please try again.', 'error');
        } finally {
          setLocalLoading(prev => ({ ...prev, [articleId]: false }));
        }
      },
    });
  };

  const handleViewPublic = (article: Article, event: React.MouseEvent) => {
    event.stopPropagation();
    if (article?.id && article.id.startsWith('art_')) {
      const wwwUrl = new URL(window.location.origin);
      wwwUrl.hostname = wwwUrl.hostname.replace(/^(app|[^.]+)\./, 'www.');
      // Use slug for better SEO, fallback to UUID if slug is not available
      const identifier = article.slug || article.id.substring(4);
      const publicUrl = `${wwwUrl.origin}/articles/${identifier}`;
      window.open(publicUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopyUrl = async (article: Article, event: React.MouseEvent) => {
    event.stopPropagation();
    if (article?.id && article.id.startsWith('art_')) {
      const wwwUrl = new URL(window.location.origin);
      wwwUrl.hostname = wwwUrl.hostname.replace(/^(app|[^.]+)\./, 'www.');
      // Use slug for better SEO, fallback to UUID if slug is not available
      const identifier = article.slug || article.id.substring(4);
      const publicUrl = `${wwwUrl.origin}/articles/${identifier}`;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(publicUrl);
        } else {
          // Fallback for non-secure contexts
          const textArea = document.createElement('textarea');
          textArea.value = publicUrl;
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        setCopySuccess(prev => ({ ...prev, [article.id]: true }));
        addToast('URL copied to clipboard!', 'success');
        setTimeout(() => setCopySuccess(prev => ({ ...prev, [article.id]: false })), 2000);
      } catch (error) {
        console.error('Failed to copy URL:', error);
        addToast('Failed to copy URL to clipboard', 'error');
      }
    }
  };

  const handlePreview = (articleId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    // Open preview in a new tab
    window.open(`/articles/preview?id=${articleId}`, `preview-${articleId}`);
  };

  const handleTagsChange = async (articleId: string, newTags: string[]) => {
    const article = articles.find(a => a.id === articleId);
    if (!article) return;

    // Check if tags actually changed
    const currentTags = article.tags || [];
    const tagsChanged = JSON.stringify(currentTags.sort()) !== JSON.stringify(newTags.sort());
    if (!tagsChanged) return;

    setLocalLoading(prev => ({ ...prev, [`tags-${articleId}`]: true }));

    try {
      await onUpdateArticleTags(articleId, newTags);
      addToast('Tags updated', 'success');
    } catch (error) {
      console.error('Error updating tags:', error);
      addToast('Failed to update tags', 'error');
    } finally {
      setLocalLoading(prev => ({ ...prev, [`tags-${articleId}`]: false }));
    }
  };

  const handleCategoryChange = async (articleId: string, newCategory: string) => {
    const article = articles.find(a => a.id === articleId);
    if (!article) return;

    // Normalize empty string to empty for comparison
    const normalizedNew = newCategory || '';
    const normalizedCurrent = article.category || '';

    if (normalizedCurrent === normalizedNew) return;

    setLocalLoading(prev => ({ ...prev, [`category-${articleId}`]: true }));

    try {
      // Update article metadata with new category (empty string means uncategorized)
      await articlesApi.updateArticle(articleId, {
        meta: {
          ...article.meta,
          category: newCategory || ''  // Use empty string for uncategorized
        }
      });

      // Update local state without full refresh
      onUpdateArticleCategory(articleId, newCategory || '');

      addToast(newCategory ? 'Category updated' : 'Category removed', 'success');
    } catch (error) {
      console.error('Error updating category:', error);
      addToast('Failed to update category', 'error');
    } finally {
      setLocalLoading(prev => ({ ...prev, [`category-${articleId}`]: false }));
    }
  };

  // Function to get status badge color
  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'archived':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <>
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={() => setConfirmState({ ...confirmState, isOpen: false })}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
      />
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="py-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-gray-500">Loading articles...</p>
          </div>
        ) : articles.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            No articles found
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <table className="min-w-full divide-y divide-gray-200 hidden md:table">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-full">
                    Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>

                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Modified
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {articles.map(article => (
                  <React.Fragment key={article.id}>
                    <tr
                      className={`bg-white border-b hover:bg-gray-50 ${selectedArticle?.id === article.id ? 'bg-blue-50' : ''} cursor-pointer`}
                      onClick={() => {
                        onArticleSelect(article);
                        toggleRowExpansion(article.id);
                      }}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-start">
                          <div className="text-gray-400 mt-1 flex-shrink-0 mr-2">
                            {expandedRows[article.id] ? (
                              <ChevronUpIcon className="h-5 w-5" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5" />
                            )}
                          </div>
                          <div className="text-sm font-medium text-gray-900 break-words">{article.title}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(article.status)}`}>
                          {article.status.charAt(0).toUpperCase() + article.status.slice(1)}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {article.category ? (categoryMap[article.category] || article.category) : 'Not categorized'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {article.tags && article.tags.length > 0 ? (
                            article.tags.slice(0, 3).map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                <TagIcon className="h-3 w-3 mr-1" />
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">No tags</span>
                          )}
                          {article.tags && article.tags.length > 3 && (
                            <span className="text-xs text-gray-500">+{article.tags.length - 3} more</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {article.lastModified ? new Date(article.lastModified).toLocaleDateString() : '-'}
                        <br />
                        {article.lastModified ? new Date(article.lastModified).toLocaleTimeString() : ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {article.status === 'published' ? (
                            <>
                              <Tooltip title="View Public Article" position="top" animation="fade" arrow={true} theme="dark">
                                <button
                                  onClick={(e) => handleViewPublic(article, e)}
                                  className="p-1 text-blue-500 hover:text-blue-700"
                                >
                                  <ExternalLinkIcon className="h-5 w-5" />
                                </button>
                              </Tooltip>
                              <Tooltip title="Copy Public URL" position="top" animation="fade" arrow={true} theme="dark">
                                <button
                                  onClick={(e) => handleCopyUrl(article, e)}
                                  className="p-1 text-gray-500 hover:text-gray-700"
                                >
                                  {copySuccess[article.id] ? (
                                    <CheckIcon className="h-5 w-5 text-green-500" />
                                  ) : (
                                    <ClipboardIcon className="h-5 w-5" />
                                  )}
                                </button>
                              </Tooltip>
                            </>
                          ) : article.status === 'draft' ? (
                            <>
                              <Tooltip title="Edit Article" position="top" animation="fade" arrow={true} theme="dark">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditArticle(article.id);
                                  }}
                                  className="p-1 text-gray-500 hover:text-gray-700"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                              </Tooltip>
                              <Tooltip title="Preview Article" position="top" animation="fade" arrow={true} theme="dark">
                                <button
                                  onClick={(e) => handlePreview(article.id, e)}
                                  className="p-1 text-gray-500 hover:text-gray-700"
                                >
                                  <EyeIcon className="h-5 w-5" />
                                </button>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title="Preview Article" position="top" animation="fade" arrow={true} theme="dark">
                              <button
                                onClick={(e) => handlePreview(article.id, e)}
                                className="p-1 text-gray-500 hover:text-gray-700"
                              >
                                <EyeIcon className="h-5 w-5" />
                              </button>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete Article" position="top" animation="fade" arrow={true} theme="dark">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteArticle(article.id);
                              }}
                              className="p-1 text-red-500 hover:text-red-700"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded row with AI summary and actions */}
                    {expandedRows[article.id] && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                              {/* AI Summary */}
                              <div className="mb-4">
                                <div className="p-4 bg-gray-50 rounded-b-lg">
                                  <h4 className="text-sm font-semibold text-gray-800">AI Summary</h4>
                                  <p className="text-sm text-gray-600 mt-1 italic">
                                    {article.status === 'published'
                                      ? (article.meta.summary || 'No summary available. Update the article to generate one.')
                                      : 'Summary is not available for non-published articles.'}
                                  </p>
                                </div>
                              </div>

                              {/* Metadata and Actions */}
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                {/* Category and Tags Management - Unified view for all statuses */}
                                <div className="mb-4">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {/* Category - Read-only for published, editable for drafts */}
                                    <div className="col-span-1 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                      <label className="block text-xs font-semibold text-gray-700 mb-2">
                                        Category
                                        {article.status === 'published' && (
                                          <span className="ml-1 text-xs text-gray-500">(locked)</span>
                                        )}
                                        {localLoading[`category-${article.id}`] && (
                                          <span className="ml-2 text-xs text-gray-500 italic">Saving...</span>
                                        )}
                                      </label>
                                      {article.status === 'published' ? (
                                        <div className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium ${article.category ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500 italic'
                                          }`}>
                                          {article.category ? (categoryMap[article.category] || article.category) : 'Not categorized'}
                                        </div>
                                      ) : (
                                        <TaggedSelect
                                          multiple={false}
                                          editable={false}
                                          value={article.category || ''}
                                          onChange={(category) => handleCategoryChange(article.id, category as string)}
                                          options={Object.entries(categoryMap).map(([slug, displayName]) => ({ value: slug, label: displayName }))}
                                          placeholder="Not categorized - click to select"
                                          disabled={localLoading[`category-${article.id}`]}
                                        />
                                      )}
                                    </div>

                                    {/* Tags - Editable for all statuses */}
                                    <div className="col-span-1 md:col-span-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                      <label className="block text-xs font-semibold text-gray-700 mb-2">
                                        Tags
                                        {localLoading[`tags-${article.id}`] && (
                                          <span className="ml-2 text-xs text-gray-500 italic">Saving...</span>
                                        )}
                                      </label>
                                      <TaggedSelect
                                        multiple
                                        editable
                                        value={article.tags || []}
                                        onChange={(tags) => handleTagsChange(article.id, tags as string[])}
                                        options={availableTags.map(tag => ({ value: tag, label: tag }))}
                                        placeholder="Add or create tags..."
                                        disabled={localLoading[`tags-${article.id}`]}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                                  {/* Actions here same as before... clone, unpublish, publish... */}
                                  {/* Clone - only for draft */}
                                  <Tooltip title="Create a new draft from this article" position="top" animation="fade" arrow={true} theme="dark">
                                    <button
                                      onClick={(e) => handleClone(article.id, e)}
                                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                      disabled={localLoading[article.id] || isLoading}
                                    >
                                      <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                                      Clone
                                    </button>
                                  </Tooltip>

                                  {/* Unpublish - only for published */}
                                  {article.status === 'published' && (
                                    <Tooltip title="Move this article back to draft status" position="top" animation="fade" arrow={true} theme="dark">
                                      <button
                                        onClick={(e) => handleUnpublish(article.id, e)}
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                        disabled={localLoading[article.id] || isLoading}
                                      >
                                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                                        Unpublish
                                      </button>
                                    </Tooltip>
                                  )}

                                  {/* Publish - only for draft */}
                                  {article.status === 'draft' && (
                                    <Tooltip title="Make this article live" position="top" animation="fade" arrow={true} theme="dark">
                                      <button
                                        onClick={(e) => handlePublish(article.id, e)}
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-75"
                                        disabled={localLoading[article.id] || isLoading}
                                      >
                                        {localLoading[article.id] ? (
                                          <>
                                            <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                                            Publishing...
                                          </>
                                        ) : (
                                          <>
                                            <ArrowUpCircleIcon className="h-4 w-4 mr-1" />
                                            Publish
                                          </>
                                        )}
                                      </button>
                                    </Tooltip>
                                  )}

                                  {/* Archive - only for published */}
                                  {article.status === 'published' && (
                                    <Tooltip title="Remove this article from public view" position="top" animation="fade" arrow={true} theme="dark">
                                      <button
                                        onClick={(e) => handleArchive(article.id, e)}
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                        disabled={localLoading[article.id] || isLoading}
                                      >
                                        <ArchiveBoxIcon className="h-4 w-4 mr-1" />
                                        Archive
                                      </button>
                                    </Tooltip>
                                  )}

                                  {/* Featured Summary - only for published */}
                                  {article.status === 'published' && onFeaturedSummary && (
                                    <Tooltip title="Generate a featured summary for content cards" position="top" animation="fade" arrow={true} theme="dark">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); onFeaturedSummary(article); }}
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                        disabled={localLoading[article.id] || isLoading}
                                      >
                                        <SparklesIcon className="h-4 w-4 mr-1" />
                                        Featured Summary
                                      </button>
                                    </Tooltip>
                                  )}

                                  {/* Share - only for published */}
                                  {article.status === 'published' && onShareArticle && (
                                    <Tooltip title="Share this article" position="top" animation="fade" arrow={true} theme="dark">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); onShareArticle(article.id); }}
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        disabled={localLoading[article.id] || isLoading}
                                      >
                                        <ShareIcon className="h-4 w-4 mr-1" />
                                        Share To...
                                      </button>
                                    </Tooltip>
                                  )}

                                  {/* Publish To - only for published */}
                                  {article.status === 'published' && (
                                    <PublishToDropdown
                                      articleId={article.id}
                                      onPublishComplete={() => loadPublicationStatus(article.id)}
                                    />
                                  )}

                                  {/* Restore - only for archived */}
                                  {article.status === 'archived' && (
                                    <Tooltip title="Return this article to a draft state" position="top" animation="fade" arrow={true} theme="dark">
                                      <button
                                        onClick={(e) => handleRestore(article.id, e)}
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        disabled={localLoading[article.id] || isLoading}
                                      >
                                        <ArrowPathIcon className="h-4 w-4 mr-1" />
                                        Restore
                                      </button>
                                    </Tooltip>
                                  )}
                                </div>
                                {/* Publication Status (below actions, only for published) */}
                                {article.status === 'published' && publicationStatus[article.id] && (
                                  <div className="mt-3 flex items-center gap-2 justify-start md:justify-end">
                                    <span className="text-xs text-gray-500">Published to:</span>
                                    <PublicationStatus publications={publicationStatus[article.id]} />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {articles.map(article => (
                <div
                  key={article.id}
                  className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${selectedArticle?.id === article.id ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => {
                    onArticleSelect(article);
                    toggleRowExpansion(article.id);
                  }}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-start flex-1 min-w-0 pr-2">
                      <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0 mt-0.5" />
                      <h3 className="text-base font-medium text-gray-900 break-words">{article.title}</h3>
                    </div>
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded-full flex-shrink-0 ${getStatusBadgeClass(article.status)}`}>
                      {article.status.charAt(0).toUpperCase() + article.status.slice(1)}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm text-gray-500 mb-4">
                    <div className="flex items-center justify-between">
                      <span>Category: {article.category ? (categoryMap[article.category] || article.category) : 'Uncategorized'}</span>
                      <span>{article.lastModified ? new Date(article.lastModified).toLocaleDateString() : '-'}</span>
                    </div>
                    {article.tags && article.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {article.tags.slice(0, 5).map((tag, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {tag}
                          </span>
                        ))}
                        {article.tags.length > 5 && (
                          <span className="text-xs text-gray-500 self-center">+{article.tags.length - 5}</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                    <div className="flex space-x-3">
                      {/* Common Quick Actions */}
                      {article.status === 'published' ? (
                        <button
                          onClick={(e) => handleViewPublic(article, e)}
                          className="text-blue-500 hover:text-blue-700 p-1"
                          title="View Public"
                        >
                          <ExternalLinkIcon className="h-5 w-5" />
                        </button>
                      ) : article.status === 'draft' ? (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); onEditArticle(article.id); }}
                            className="text-gray-500 hover:text-gray-700 p-1"
                            title="Edit"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => handlePreview(article.id, e)}
                            className="text-gray-500 hover:text-gray-700 p-1"
                            title="Preview"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={(e) => handlePreview(article.id, e)}
                          className="text-gray-500 hover:text-gray-700 p-1"
                          title="Preview"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); onDeleteArticle(article.id); }}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Expand Toggle */}
                    <button
                      onClick={(e) => toggleRowExpansion(article.id, e)}
                      className="text-gray-400 p-1"
                    >
                      {expandedRows[article.id] ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                    </button>
                  </div>

                  {/* Expanded Details in Card View - Reuse logic if possible, or simplified */}
                  {expandedRows[article.id] && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      {/* Simplified expanded view for mobile */}
                      <div className="space-y-4">
                        <div>
                          <span className="text-xs font-semibold text-gray-700 uppercase">Tags</span>
                          <div className="mt-1">
                            <TaggedSelect
                              multiple
                              editable
                              value={article.tags || []}
                              onChange={(tags) => handleTagsChange(article.id, tags as string[])}
                              options={availableTags.map(tag => ({ value: tag, label: tag }))}
                              placeholder="Add tags..."
                              disabled={localLoading[`tags-${article.id}`]}
                            />
                          </div>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-700 uppercase">Category</span>
                          <div className="mt-1">
                            {article.status === 'published' ? (
                              <span className="text-sm text-gray-600">{article.category || 'Uncategorized'}</span>
                            ) : (
                              <TaggedSelect
                                multiple={false}
                                editable={false}
                                value={article.category || ''}
                                onChange={(category) => handleCategoryChange(article.id, category as string)}
                                options={Object.entries(categoryMap).map(([slug, displayName]) => ({ value: slug, label: displayName }))}
                                placeholder="Select Category"
                                disabled={localLoading[`category-${article.id}`]}
                              />
                            )}
                          </div>
                        </div>

                        {/* Full Actions List for Mobile Expansion */}
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {article.status === 'draft' && (
                            <button
                              onClick={(e) => handlePublish(article.id, e)}
                              className="flex items-center justify-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                            >
                              <ArrowUpCircleIcon className="h-4 w-4 mr-1" /> Publish
                            </button>
                          )}
                          {/* Add other secondary actions here as needed for mobile */}
                          <button
                            onClick={(e) => handleClone(article.id, e)}
                            className="flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4 mr-1" /> Clone
                          </button>

                          {article.status === 'published' && (
                            <button
                              onClick={(e) => handleUnpublish(article.id, e)}
                              className="flex items-center justify-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                            >
                              <ArrowPathIcon className="h-4 w-4 mr-1" /> Unpublish
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
